import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {useCallback, useEffect, useRef, useState} from "react";
import {DraftLine, diffDraft, draftLinesFrom, duplicateLine, hasChanges, newDraftLine, NewLineDefaults, rebaseDraft} from "./folioDraft.util";

interface Options {
    folioId?: string
    /** The folio's saved lines; undefined while they load. */
    serverItems?: FolioItem[]
    /** A draft folio whose lines may change now. Otherwise the draft always shows the saved lines. */
    editable: boolean
    /** Reads the saved lines again after a save. */
    refetch: () => Promise<FolioItem[] | undefined>
    /** After a save that wrote something, e.g. to reload the total. */
    onSaved?: () => void
}

interface DraftState {
    folioId?: string
    /** The saved lines the draft started from - what a save compares against. */
    baseline: FolioItem[]
    lines: DraftLine[]
}

export interface FolioDraft {
    /** The lines belong to the current folio (false while they load). */
    ready: boolean
    lines: DraftLine[]
    isDirty: boolean
    saving: boolean
    /** Why the last save failed, until the next one. */
    error?: unknown
    clearError: () => void
    /** Drops what isn't saved - the way out when saving keeps failing. */
    discard: () => void
    update: (key: string, patch: Partial<FolioItem>) => void
    /** Adds a "Choose a service" line and returns its key. */
    add: (defaults: Omit<NewLineDefaults, 'folioId'>) => string
    remove: (key: string) => void
    duplicate: (key: string) => void
    /** A field of the line was left: its errors show from now on. */
    touch: (key: string) => void
    showsErrors: (key: string) => boolean
    /** Finalize was attempted: every line shows its errors. */
    revealErrors: () => void
    /** Saves the draft; true when everything is saved, or nothing had to be. */
    persist: () => Promise<boolean>
}

/**
 * The charge dialog's lines, edited locally and saved in one go: the saved lines replace the draft
 * whenever it has nothing unsaved, and persist() writes the differences one call after the other.
 */
export const useFolioDraft = ({folioId, serverItems, editable, refetch, onSaved}: Options): FolioDraft => {
    const [state, setState] = useState<DraftState>({baseline: [], lines: []});
    const [touched, setTouched] = useState<Set<string>>(() => new Set());
    const [allErrors, setAllErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<unknown>();
    const stateRef = useRef(state);
    stateRef.current = state;
    const inFlight = useRef<Promise<boolean>>();

    // Newer saved lines replace the draft - or, with unsaved edits, the edits move on top of them (a
    // locked draft or another folio always starts over). Done while rendering, so no effect ever sees
    // the draft behind its lines. A running save settles the draft itself when it's done.
    const [synced, setSynced] = useState<{folioId?: string, serverItems?: FolioItem[], editable?: boolean}>({});
    const outdated = synced.folioId !== folioId || synced.serverItems !== serverItems || synced.editable !== editable;
    if (folioId && serverItems && outdated && !inFlight.current) {
        setSynced({folioId, serverItems, editable});
        const keepEdits = state.folioId === folioId && editable && hasChanges(diffDraft(state.baseline, state.lines));
        setState({
            folioId,
            baseline: serverItems,
            lines: keepEdits ? rebaseDraft(state.baseline, state.lines, serverItems) : draftLinesFrom(serverItems),
        });
    }

    const ready = !!folioId && state.folioId === folioId;
    const isDirty = ready && editable && hasChanges(diffDraft(state.baseline, state.lines));

    useEffect(() => {
        setTouched(new Set());
        setAllErrors(false);
        setError(undefined);
    }, [folioId]);

    const setLines = useCallback((next: (lines: DraftLine[]) => DraftLine[]) => {
        setState(current => ({...current, lines: next(current.lines)}));
    }, []);

    const update = useCallback((key: string, patch: Partial<FolioItem>) => setLines(lines => lines.map(line => (
        line.key === key ? {...line, item: {...line.item, ...patch}} : line
    ))), [setLines]);

    const add = (defaults: Omit<NewLineDefaults, 'folioId'>) => {
        const line = newDraftLine({...defaults, folioId});
        setLines(lines => [...lines, line]);
        return line.key;
    };

    const remove = (key: string) => setLines(lines => lines.filter(line => line.key !== key));

    const duplicate = (key: string) => setLines(lines => {
        const index = lines.findIndex(line => line.key === key);
        if (index < 0) return lines;
        const next = [...lines];
        next.splice(index + 1, 0, duplicateLine(lines[index]));
        return next;
    });

    const touch = useCallback((key: string) => setTouched(current => (
        current.has(key) ? current : new Set(current).add(key)
    )), []);

    const save = async (): Promise<boolean> => {
        const current = stateRef.current;
        if (!editable || !folioId || current.folioId !== folioId) return true;
        const changes = diffDraft(current.baseline, current.lines);
        if (!hasChanges(changes)) return true;

        setSaving(true);
        setError(undefined);
        // Local key -> saved id, so a retry after a failure doesn't create a line twice
        const created = new Map<string, string>();
        let failure: unknown;
        try {
            for (const item of changes.update) await FolioItemService.updateFolioItem(item);
            for (const line of changes.create) {
                const saved = await FolioItemService.createFolioItem(line.item);
                if (saved?.id) created.set(line.key, saved.id);
            }
            if (changes.delete.length) await FolioItemService.deleteFolioItem({id: changes.delete});
        } catch (e) {
            failure = e;
        }

        let savedItems: FolioItem[] | undefined;
        try {
            savedItems = await refetch();
        } catch (e) {
            failure = failure ?? e;
        }
        onSaved?.();

        setState(previous => {
            if (previous.folioId !== folioId) return previous;
            if (!failure && savedItems) return {folioId, baseline: savedItems, lines: draftLinesFrom(savedItems)};
            // Keep what isn't saved, on top of what is saved now
            const lines = previous.lines.map(line => {
                const id = created.get(line.key);
                return id ? {key: id, item: {...line.item, id}} : line;
            });
            return savedItems
                ? {folioId, baseline: savedItems, lines: rebaseDraft(previous.baseline, lines, savedItems)}
                : {...previous, lines};
        });
        if (created.size) {
            setTouched(previous => {
                const next = new Set<string>();
                previous.forEach(key => next.add(created.get(key) ?? key));
                return next;
            });
        }
        setError(failure);
        setSaving(false);
        return !failure;
    };

    // One save at a time: a second caller waits for the running one
    const persist = () => {
        if (!inFlight.current) {
            inFlight.current = save().finally(() => {
                inFlight.current = undefined;
            });
        }
        return inFlight.current;
    };

    return {
        ready,
        lines: ready ? state.lines : [],
        isDirty,
        saving,
        error,
        clearError: () => setError(undefined),
        discard: () => {
            setState(current => ({...current, lines: draftLinesFrom(current.baseline)}));
            setError(undefined);
        },
        update,
        add,
        remove,
        duplicate,
        touch,
        showsErrors: key => allErrors || touched.has(key),
        revealErrors: () => setAllErrors(true),
        persist,
    };
};
